import { useEffect, useState } from "react";
import { api, Book, Incident, Manifest, RiskState } from "../api";
import { signedUsd, usd, usdCompact } from "../lib/money";

const CONFIRM_PHRASE = "FLATTEN AND HALT";
const GO_LIVE = "GO LIVE";

const LIVE_LABEL: Record<string, string> = { live: "Live · Daily", live_long_term: "Live · Growth" };

export default function Risk(_: { manifest: Manifest }) {
  const [state, setState] = useState<RiskState | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [liveBooks, setLiveBooks] = useState<Book[]>([]);
  const [goLive, setGoLive] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fastest poll in the app: this is the incident-response page — it used to
  // fetch once and never again, so it wouldn't update DURING an incident
  // (Kaizen D5/F7's worst case).
  useEffect(() => {
    void load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      setState(await api.get<RiskState>("/api/risk/state"));
      setIncidents(await api.get<Incident[]>("/api/risk/incidents"));
      const books = await api.get<Book[]>("/api/trading/books");
      setLiveBooks(books.filter((b) => b.account_mode === "live"));
    } catch (e) { setError((e as Error).message); }
  }

  async function toggleLive(bookKey: string, enable: boolean) {
    setBusy(true);
    setError(null);
    try {
      if (enable) await api.post(`/api/trading/books/${bookKey}/enable`, { mode: "live", confirm: goLive });
      else await api.post(`/api/trading/books/${bookKey}/disable`);
      setGoLive("");
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function kill() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/risk/kill", { confirm, reason: reason || undefined });
      setConfirm("");
      setReason("");
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function resume() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/risk/resume");
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function resumeEntries() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/risk/resume-entries");
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function resolveIncident(id: string) {
    try {
      await api.post(`/api/risk/incidents/${id}/resolve`);
      await load();
    } catch (e) { setError((e as Error).message); }
  }

  const halted = state?.tenant_halted || state?.halted;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold">Risk &amp; Limits</h1>
      {error && <div className="text-down text-sm">{error}</div>}

      {liveBooks.length > 0 && (
        <div className="card border-edge space-y-4">
          <div>
            <h2 className="font-bold">Live books</h2>
            <p className="text-sm text-slate-400 mt-1">
              Paper books always run. Each <b className="text-down">live book</b> is an independent firm
              against your funded accounts — its own board, desk, and ledger, including its crypto and
              forex sleeves. Risk floors, budget caps, and the kill switch apply identically.
            </p>
          </div>
          {liveBooks.map((b) => (
            <div key={b.book_key} className={`rounded-lg border p-3 space-y-2 ${b.enabled ? "border-down/60" : "border-edge"}`}>
              <div className="flex items-center gap-3">
                <span className="font-semibold">{LIVE_LABEL[b.book_key] ?? b.label}</span>
                <span className={`badge ${b.enabled ? "bg-down/20 text-down" : "bg-slate-700 text-slate-400"}`}>
                  {b.enabled ? "ENABLED" : "OFF"}
                </span>
              </div>
              {b.enabled ? (
                <button className="btn" disabled={busy} onClick={() => toggleLive(b.book_key, false)}>
                  {busy ? "…" : "Disable"}
                </button>
              ) : (
                <div className="flex flex-wrap gap-2 items-center">
                  <input className="input max-w-48 font-mono" placeholder={GO_LIVE}
                         value={goLive} onChange={(e) => setGoLive(e.target.value)} />
                  <button className="btn btn-danger" disabled={busy || goLive !== GO_LIVE}
                          onClick={() => toggleLive(b.book_key, true)}>
                    {busy ? "…" : "Enable"}
                  </button>
                  <span className="text-xs text-slate-500">
                    type <span className="font-mono text-down">{GO_LIVE}</span> to arm
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {state && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card space-y-2">
            <h2 className="label">Day state — {state.trading_day}</h2>
            <Row k="Start equity" v={state.start_equity ? usd(state.start_equity) : "not marked"} />
            <Row k="Realized P&L" v={signedUsd(state.realized_pnl)} />
            <Row k="Day trades used (PDT)" v={`${state.day_trades_used} / 3`} />
            <Row k="Losing streak" v={`${state.losing_streak} days`} />
            <Row k="No new entries" v={state.no_new_entries ? "YES (wind-down)" : "no"} warn={state.no_new_entries} />
            <Row k="Flattened today" v={state.flattened ? "yes" : "no"} />
            <Row k="Daily-loss halt" v={state.halted ? "ACTIVE" : "clear"} warn={state.halted} />
            <Row k="Kill switch" v={state.tenant_halted ? `ACTIVE — ${state.halt_reason ?? ""}` : "clear"} warn={state.tenant_halted} />
            <Row k="Weak-edge entries gate" v={state.entries_blocked ? "BLOCKED (live only)" : "clear"} warn={state.entries_blocked} />
          </div>

          <div className="card space-y-2">
            <h2 className="label">Deterministic floors (server-enforced)</h2>
            <Row k="Budget cap" v={state.floors.budget_cap_usd != null ? usdCompact(state.floors.budget_cap_usd) : "—"} />
            <Row k="Daily max loss" v={`${state.floors.daily_loss_pct}% of NAV`} />
            <Row k="Max single position" v={`${state.floors.max_position_pct}% of NAV`} />
            <Row k="Max gross exposure" v={`${state.floors.max_gross_exposure_pct}%`} />
            <Row k="Auto-pause after" v={`${state.floors.auto_pause_losing_days} losing days`} />
            <p className="text-xs text-slate-500 pt-2">
              Floors live in the server environment. Firm-config can lower them, never raise them.
              No agent — including the board — can override a risk-engine rejection or the EOD flatten.
            </p>
          </div>
        </div>
      )}

      <div className="card border-down/40 space-y-3">
        <h2 className="font-bold text-down">Kill switch</h2>
        <p className="text-sm text-slate-400">
          Cancels every open order, flattens every position at market, and halts the firm.
          Independent of the desk and board. Type <span className="font-mono text-down">{CONFIRM_PHRASE}</span> to arm.
        </p>
        <div className="flex flex-wrap gap-2">
          <input className="input max-w-60 font-mono" placeholder={CONFIRM_PHRASE}
                 value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <input className="input max-w-72" placeholder="reason (optional)"
                 value={reason} onChange={(e) => setReason(e.target.value)} />
          <button className="btn btn-danger" onClick={kill}
                  disabled={busy || confirm !== CONFIRM_PHRASE}>
            {busy ? "…" : "KILL"}
          </button>
          {halted && (
            <button className="btn" onClick={resume} disabled={busy}>
              Resume trading (human review)
            </button>
          )}
        </div>
      </div>

      {state?.entries_blocked && (
        <div className="card border-down/40 space-y-3">
          <h2 className="font-bold text-down">Weak-edge entries gate — live book</h2>
          <p className="text-sm text-slate-400">{state.entries_blocked_reason}</p>
          <p className="text-sm text-slate-400">
            New entries on the live book are blocked. Existing positions still manage normally
            (stops, targets, time-exits). This does not clear itself — review the record, then
            resume.
          </p>
          <button className="btn" onClick={resumeEntries} disabled={busy}>
            {busy ? "…" : "Resume live entries"}
          </button>
        </div>
      )}

      <div className="card">
        <h2 className="label">Incidents</h2>
        <div className="space-y-2">
          {incidents.map((i) => (
            <div key={i.id} className="flex items-start gap-3 text-sm">
              <span className={`badge ${i.severity === "critical" ? "bg-down/20 text-down" : "bg-gold/20 text-gold"}`}>{i.severity}</span>
              <div className="flex-1">
                <div className={i.status === "resolved" ? "text-slate-500 line-through" : ""}>{i.title}</div>
                {i.detail && <div className="text-xs text-slate-500">{i.detail.slice(0, 200)}</div>}
              </div>
              {i.blocks_open && i.status === "open" && <span className="badge bg-down/20 text-down">blocks open</span>}
              {i.status === "open" && (
                <button className="btn text-xs" onClick={() => resolveIncident(i.id)}>Resolve</button>
              )}
            </div>
          ))}
          {incidents.length === 0 && <div className="text-slate-600 text-sm">no incidents</div>}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, warn }: { k: string; v: string; warn?: boolean }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-400">{k}</span>
      <span className={`font-mono ${warn ? "text-down font-bold" : ""}`}>{v}</span>
    </div>
  );
}
